import React from 'react';
import { useHistory } from 'react-router-dom';

const CheckIcon = () => (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
        <path d="M13.3333 4L6.00001 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const EntrepreneurTabs = ({ activeTab }) => {
    const history = useHistory();
    const isLoggedIn = !!sessionStorage.getItem('ssoToken');

    const isCreighton = window.location.href.toLowerCase().includes("creighton");
    const basePath = isCreighton ? '/creighton' : '/entrepreneur';

    const tabs = [
        {
            id: 'welcome',
            label: 'Welcome',
            onClick: () => history.push(basePath),
        },
        {
            id: 'registration',
            label: 'Registration',
            onClick: () => history.push(`${basePath}/auth-gateway`),
        },
        {
            id: 'ris',
            label: 'RIS',
            onClick: () => isLoggedIn
                ? history.push(`${basePath}/research-consent-form`)
                : history.push(`${basePath}/auth-gateway`),
        },
        {
            id: 'assessment',
            label: 'Assessment',
            onClick: () => isLoggedIn
                ? history.push(`${basePath}/introduction`)
                : history.push(`${basePath}/auth-gateway`),
        },
        {
            id: 'materials',
            label: 'Upload Resume',
            onClick: () => isLoggedIn
                ? history.push(`${basePath}/research-consent-upload`)
                : history.push(`${basePath}/auth-gateway`),
        },
        {
            id: 'report',
            label: 'Report',
            onClick: () => isLoggedIn
                ? history.push(`${basePath}/report`)
                : history.push(`${basePath}/auth-gateway`),
        },
        {
            id: 'invite',
            label: 'Invite Others',
            onClick: () => isLoggedIn
                ? history.push(`${basePath}/share-assessment-link`)
                : history.push(`${basePath}/auth-gateway`),
        },
    ];

    const activeIndex = tabs.findIndex(t => t.id === activeTab);
    const stepNumber = activeIndex === -1 ? 1 : activeIndex + 1;
    const activeTabData = tabs[activeIndex >= 0 ? activeIndex : 0];
    const isConsentOrLater = activeIndex >= 2;

    return (
        <>
            <div
                className="fixed top-[128px] lg:top-[75px] left-0 right-0 w-full z-[999] dark:bg-[#f1f6fbd9] bg-[#f1f6fbd9] backdrop-blur-md border-b border-[rgba(10,31,68,0.06)]"
            >
                <div className="max-w-[var(--container)] mx-auto px-4 sm:px-6 md:px-8">

                    {/* ── MOBILE (< sm): compact circles + active label ── */}
                    <div className="flex sm:hidden items-center gap-3 py-3">
                        {/* Step dots row */}
                        <div className="flex items-center flex-1 min-w-0">
                            {tabs.map((tab, index) => {
                                const isActive = activeTab === tab.id;
                                const isCompleted = index < activeIndex;
                                const isLast = index === tabs.length - 1;
                                const canNavigate = isActive || (!isConsentOrLater && (tab.id === 'welcome' || tab.id === 'registration' || tab.id === 'ris'));
                                return (
                                    <React.Fragment key={tab.id}>
                                        <button
                                            type="button"
                                            onClick={canNavigate ? tab.onClick : undefined}
                                            aria-label={tab.label}
                                            className={`
                                                flex items-center justify-center shrink-0
                                                w-6 h-6 rounded-full text-[0.65rem] font-bold
                                                transition-all duration-200
                                                ${isActive
                                                    ? 'bg-[#1B75BC] text-white ring-[3px] ring-[#1B75BC]/20'
                                                    : isCompleted
                                                        ? 'bg-[#20A577] text-white'
                                                        : 'bg-white border border-gray-300 text-gray-400'
                                                }
                                                cursor-pointer
                                            `}
                                        >
                                            {isCompleted ? <CheckIcon /> : index + 1}
                                        </button>

                                        {!isLast && (
                                            <div
                                                className={`flex-1 h-[1.5px] mx-1 ${isCompleted ? 'bg-[#20A577]' : 'bg-gray-200'}`}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* Active step label + counter */}
                        <div className="flex items-center gap-1.5 shrink min-w-0 pl-1 border-l border-gray-200">
                            <span className="text-[#1B75BC] text-[0.68rem] font-bold tracking-wider uppercase leading-none truncate min-w-0">
                                {activeTabData?.label}
                            </span>
                            <span className="text-gray-400 text-[0.6rem] font-mono leading-none shrink-0">
                                {String(stepNumber).padStart(2, '0')}/{String(tabs.length).padStart(2, '0')}
                            </span>
                        </div>
                    </div>

                    {/* ── TABLET / DESKTOP (sm+): full stepper with labels ── */}
                    <div className="hidden sm:flex items-center justify-between py-5 px-6 gap-4 overflow-x-auto scrollbar-hide">
                        <div className="flex items-center min-w-max">
                            {tabs.map((tab, index) => {
                                const isActive = activeTab === tab.id;
                                const isCompleted = index < activeIndex;
                                const isLast = index === tabs.length - 1;
                                const canNavigate = isActive || (!isConsentOrLater && (tab.id === 'welcome' || tab.id === 'registration' || tab.id === 'ris'));
                                const num = index + 1;

                                return (
                                    <React.Fragment key={tab.id}>
                                        <div
                                            className="flex items-center gap-2 md:gap-3 shrink-0 transition-opacity duration-200 cursor-pointer"
                                            onClick={canNavigate ? tab.onClick : undefined}
                                        >
                                            {/* Circle */}
                                            {isActive ? (
                                                <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#1B75BC] text-white font-bold text-sm ring-4 ring-[#1B75BC]/20 shrink-0">
                                                    {num}
                                                </div>
                                            ) : isCompleted ? (
                                                <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#20A577] text-white border border-[#20A577] shrink-0">
                                                    <CheckIcon />
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white border border-gray-300 text-[#6B7280] font-semibold text-sm shrink-0">
                                                    {num}
                                                </div>
                                            )}

                                            {/* Label — hide on sm for crowded screens, show md+ */}
                                            <span className={`
                                                hidden sm:block
                                                text-xs md:text-sm lg:text-base font-semibold tracking-wide whitespace-nowrap
                                                ${isActive ? 'text-[#05163D] font-bold' : isCompleted ? 'text-[#20A577]' : 'text-gray-500'}
                                            `}>
                                                {tab.label}
                                            </span>
                                        </div>

                                        {/* Connector */}
                                        {!isLast && (
                                            <div
                                                className={`flex-1 h-[1.5px] mx-2 sm:mx-3 md:mx-5 min-w-[0.75rem] ${isCompleted ? 'bg-[#20A577]' : 'bg-gray-200'}`}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* Step counter — lg only */}
                        <div className="hidden lg:block text-xs font-semibold tracking-widest text-gray-400 font-mono shrink-0 ml-4">
                            STEP {String(stepNumber).padStart(2, '0')} / {String(tabs.length).padStart(2, '0')}
                        </div>
                    </div>

                </div>
            </div>

            {/* Spacer to offset fixed header */}
            <div className={`w-full h-[60px] sm:h-[80px] shrink-0 block print:hidden ${activeTab === 'welcome' ? 'bg-transparent' : 'bg-[#F5F8FE] dark:bg-[#F5F8FE]'}`} />
        </>
    );
};

export default EntrepreneurTabs;
